import React from "react";
import { ArrowLeft } from "lucide-react";
import BackButton from "@/components/BackButton";

/**
 * Standard Installer Portal master/detail layout.
 *
 * ┌───────────────────────────────────────────────────────────┐
 * │ Header (title + primary CTA)                              │
 * ├───────────────────────────────────────────────────────────┤
 * │ Toolbar (search + filter chips)                           │
 * ├─────────────────────┬─────────────────────────────────────┤
 * │ List (35–40%)       │ Detail (60–65%)                     │
 * │ scrolls             │ scrolls                             │
 * │                     │                                     │
 * └─────────────────────┴─────────────────────────────────────┘
 *
 * On < lg (1024px) the detail panel takes over the viewport
 * once a record is selected. Below `md` it stays as-is for
 * the mobile phone layout which is intentionally single-pane.
 */
export default function MasterDetailLayout({
    title,
    eyebrow,
    icon: Icon,
    primaryAction = null,
    backHref,
    backLabel = "Back",
    toolbar = null,
    listSummary = null,
    list,
    detail,
    hasSelection = false,
    onClearSelection = null,
    detailHeader = null,
    testid = "master-detail",
}) {
    return (
        <div
            className="h-full flex flex-col overflow-hidden bg-[#F5F5F7]"
            data-testid={testid}
        >
            {/* Top bar: eyebrow, title, primary action */}
            <div className="px-4 md:px-8 pt-5 md:pt-7 pb-4 bg-white border-b border-[#E5E7EB]">
                {backHref && (
                    <div className="mb-3">
                        <BackButton fallbackHref={backHref} label={backLabel} testid={`${testid}-back`} />
                    </div>
                )}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        {Icon && (
                            <div className="w-11 h-11 rounded-lg bg-[#E1E6F0] flex items-center justify-center text-[#131B2F] shrink-0">
                                <Icon size={20} strokeWidth={1.75} />
                            </div>
                        )}
                        <div className="min-w-0">
                            {eyebrow && (
                                <div className="text-[10px] text-[#4B5565] tracking-[0.16em] uppercase font-medium">
                                    {eyebrow}
                                </div>
                            )}
                            <h1 className="text-xl md:text-2xl text-[#131B2F] font-semibold truncate">
                                {title}
                            </h1>
                        </div>
                    </div>
                    {primaryAction && (
                        <div className="shrink-0">{primaryAction}</div>
                    )}
                </div>
                {toolbar && <div className="mt-4">{toolbar}</div>}
            </div>

            {/* Two-pane content */}
            <div className="flex-1 min-h-0 flex overflow-hidden">
                {/* List pane */}
                <div
                    className={`${
                        hasSelection ? "hidden lg:flex" : "flex"
                    } flex-col w-full lg:w-[38%] xl:w-[34%] lg:min-w-[340px] lg:max-w-[460px] border-r border-[#E5E7EB] bg-white`}
                >
                    {listSummary && (
                        <div className="px-4 md:px-5 py-2.5 border-b border-[#E5E7EB] bg-[#FAFAFC] text-[11px] text-[#4B5565] flex items-center gap-3">
                            {listSummary}
                        </div>
                    )}
                    <div className="flex-1 overflow-y-auto">{list}</div>
                </div>

                {/* Detail pane */}
                <div
                    className={`${
                        hasSelection ? "flex" : "hidden lg:flex"
                    } flex-col flex-1 min-w-0 bg-[#F5F5F7]`}
                >
                    {hasSelection && onClearSelection && (
                        <button
                            type="button"
                            onClick={onClearSelection}
                            className="lg:hidden inline-flex items-center gap-1.5 text-xs text-[#4B5565] hover:text-[#131B2F] px-4 pt-3 self-start"
                        >
                            <ArrowLeft size={14} strokeWidth={1.75} /> Back to list
                        </button>
                    )}
                    {detailHeader && (
                        <div className="px-4 md:px-6 py-4 bg-white border-b border-[#E5E7EB]">
                            {detailHeader}
                        </div>
                    )}
                    <div className="flex-1 overflow-y-auto">{detail}</div>
                </div>
            </div>
        </div>
    );
}
